import { useMsal } from "@azure/msal-react";
import { SbbLoadingIndicator } from "@sbb-esta/lyne-react/loading-indicator";
import { useQuery } from "@tanstack/react-query";
import { ErrorCode } from "components/ErrorCode";
import { getErrorCode, getRequestUrl, isCancelled } from "helpers";
import { createLogger } from "logging";
import { Permissions } from "models/Permissions";
import { useTranslations } from "providers";
import { useApi } from "providers";
import React, { PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useState } from "react";

import { PermissionsContext } from "./PermissionsContext";

const logger = createLogger("PermissionsProvider");

function setBodyClass(className: string, after: boolean) {
  const classList = document.body.classList;
  const before = classList.contains(className);
  if (after !== before) {
    logger.debug(`setBodyClass(): body.${className} changed to ${after} (was: ${before})`);
    if (after) classList.add(className);
    else classList.remove(className);
  }
}

export const PermissionsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const { permissionsApi } = useApi();
  const [permissions, setPermissions] = useState<Permissions>();
  const [errorCode, setErrorCode] = useState(0);

  const { instance, accounts } = useMsal();
  useEffect(() => {
    startTransition(() => {
      setErrorCode(0);
    });
  }, [instance, accounts]);

  const handleHttpError = useCallback((error?: unknown) => {
    if (error == null || error == undefined) {
      return;
    }

    const errorCode = getErrorCode(error);
    const requestUrl = getRequestUrl(error);
    logger.debug(`handleHttpError(): `, errorCode, requestUrl);
    setErrorCode(errorCode);
  }, []);

  const queryResult = useQuery({
    queryKey: [`Permissions|${i18n.currentLanguage}`],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      let data: Permissions;
      try {
        data = await permissionsApi.getPermissions(signal);
        setPermissions(data);
        setErrorCode(0);
      } catch (error) {
        if (!isCancelled(error)) {
          handleHttpError(error);
        }
        throw error;
      }

      logger.trace("Loaded:", data);
      return data;
    },
    enabled: !!i18n.currentLanguage,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });

  const isError = !!errorCode;
  const isLoading = queryResult.isFetching && !errorCode;
  const isSuccess = !queryResult.isFetching && !errorCode;

  const contextValue = useMemo(
    () => ({ permissions, setPermissions, setErrorCode, handleHttpError }),
    [permissions, setPermissions, setErrorCode, handleHttpError]
  );

  useEffect(() => {
    if (!permissions) return;
    setBodyClass("show-administrator", permissions.isAdministrator);
    setBodyClass("show-deleted", permissions.showDeleted);
    setBodyClass("show-permissions", permissions.showPermissions);
    setBodyClass("show-inactive", permissions.showInactive);
  }, [permissions]);

  return (
    <PermissionsContext.Provider value={contextValue}>
      {isError && <ErrorCode errorCode={errorCode} />}
      {isLoading && <SbbLoadingIndicator size="xxl" />}
      {isSuccess && children}
    </PermissionsContext.Provider>
  );
};
